import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useRouter } from 'next/router'
import { Routes } from '@/core/routes'
import { useOverlay } from '@/providers/showOverlayNewProvider'
import useDisableScroll from 'hooks/useDisableScroll'
import { useRecentSearches } from 'hooks/useRecentSearches'
import AiSearchBox from './aiSearchBox'
import AiSearchDiscovery from './aiSearchDiscovery'
import { useDictation } from 'hooks/useDictation'
import { getLocale } from '@/utils/localeHelper'

interface ProductSearchAiProps {
  isMobile?: boolean
  isCompact?: boolean
  expandToLeft?: boolean
}

const ProductSearchAi: FC<ProductSearchAiProps> = ({
  isMobile,
  isCompact,
  expandToLeft
}) => {
  const router = useRouter()
  const { setShowOverlayNew, setIsAiSearchOpen } = useOverlay()
  const [query, setQuery] = useState<string>('')
  const { recentSearches, addRecentSearch, removeRecentSearch } =
    useRecentSearches()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const isSearchingRef = useRef(false)
  isSearchingRef.current = isSearching
  const dictation = useDictation(setQuery, getLocale(router.locale), (text) =>
    handleSubmit(text)
  )

  const urlSearchQuery =
    router.route === Routes.search.replace(/\/$/, '')
      ? router.query.search?.toString() ?? ''
      : ''

  useEffect(() => {
    if (urlSearchQuery) {
      setQuery(urlSearchQuery)
    }
  }, [urlSearchQuery])

  const containerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const openSearch = () => {
    setIsOpen(true)

    if (isMobile) {
      setIsAiSearchOpen(true)
    }
  }

  const collapse = () => {
    if (isSearchingRef.current) {
      return
    }

    dictation.stop()
    setIsOpen(false)

    if (isMobile) {
      setIsAiSearchOpen(false)
      textareaRef.current?.blur()
    }
  }

  const clearSearchQuery = () => {
    dictation.stop()
    dictation.clearError()
    setQuery('')
    openSearch()
    textareaRef.current?.focus()
  }

  const dictate = () => {
    openSearch()

    if (!dictation.listening) {
      setQuery('')
    }

    dictation.start('')
  }

  const handleSubmit = async (searchValue = query) => {
    const value = searchValue.trim()

    if (isSearchingRef.current) {
      return
    }

    if (!value) {
      textareaRef.current?.focus()

      return
    }

    dictation.stop()
    dictation.clearError()
    addRecentSearch(value)
    setQuery(value)
    setIsSearching(true)
    textareaRef.current?.blur()

    try {
      if (/^\d+$/.test(value)) {
        await router.push(`${Routes.product}/${value}`)
      } else {
        await router.push({
          pathname: Routes.search,
          query: { search: value }
        })
      }
    } catch {
      // zrušená alebo zlyhaná navigácia: pole len odomknem
    } finally {
      setIsSearching(false)
      setIsOpen(false)
      setShowOverlayNew(false)
      setIsAiSearchOpen(false)
    }
  }

  useDisableScroll(isMobile && isOpen)

  const panelRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)

  const [panelHeight, setPanelHeight] = useState<number | null>(null)

  const measurePanel = useCallback(() => {
    const panel = panelRef.current

    if (!panel) {
      return
    }

    const viewport = window.visualViewport
    const viewportHeight = viewport?.height ?? window.innerHeight
    const topInViewport =
      panel.getBoundingClientRect().top - (viewport?.offsetTop ?? 0)
    const available = viewportHeight - topInViewport

    setPanelHeight(Math.max(0, Math.min(available, viewportHeight)))
  }, [])

  useEffect(() => {
    if (!isMobile || !isOpen) {
      setPanelHeight(null)

      return
    }

    measurePanel()

    const viewport = window.visualViewport

    window.addEventListener('resize', measurePanel)
    window.addEventListener('scroll', measurePanel)
    viewport?.addEventListener('resize', measurePanel)
    viewport?.addEventListener('scroll', measurePanel)

    return () => {
      window.removeEventListener('resize', measurePanel)
      window.removeEventListener('scroll', measurePanel)
      viewport?.removeEventListener('resize', measurePanel)
      viewport?.removeEventListener('scroll', measurePanel)
    }
  }, [isMobile, isOpen, measurePanel])

  useEffect(() => {
    const bar = barRef.current

    if (!isMobile || !isOpen || !bar) {
      return
    }

    const observer = new ResizeObserver(measurePanel)
    observer.observe(bar)

    return () => observer.disconnect()
  }, [isMobile, isOpen, measurePanel])

  useEffect(() => () => setIsAiSearchOpen(false), [setIsAiSearchOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleOutsidePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!isMobile && !containerRef.current?.contains(event.target as Node)) {
        collapse()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        collapse()
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown)
    document.addEventListener('touchstart', handleOutsidePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    router.events.on('routeChangeStart', collapse)

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown)
      document.removeEventListener('touchstart', handleOutsidePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      router.events.off('routeChangeStart', collapse)
    }
  }, [isOpen])

  const searchBox = (
    <AiSearchBox
      textareaRef={textareaRef}
      value={query}
      isExpanded={isOpen}
      isListening={dictation.listening}
      isDictationSupported={dictation.isSupported}
      onChange={(value) => {
        setQuery(value)
        dictation.clearError()
      }}
      onExpand={openSearch}
      onCollapse={collapse}
      onClear={clearSearchQuery}
      onDictate={dictate}
      onSubmit={() => handleSubmit()}
      isCompact={isCompact}
      isSearching={isSearching}
    />
  )

  const discovery = (isWide?: boolean) => (
    <>
      {dictation.error && (
        <StyledError role="alert">{dictation.error}</StyledError>
      )}
      <AiSearchDiscovery
        recentSearches={recentSearches}
        onSearch={handleSubmit}
        onRemoveRecent={removeRecentSearch}
        onClose={isMobile ? collapse : undefined}
        isWide={isWide}
      />
    </>
  )

  if (isMobile) {
    return (
      <StyledContainer ref={containerRef} isMobile data-open={isOpen}>
        <StyledBarWrapper ref={barRef} isMobile isOpen={isOpen}>
          {searchBox}
        </StyledBarWrapper>
        {isOpen && (
          <StyledMobilePanel
            ref={panelRef}
            style={panelHeight ? { height: `${panelHeight}px` } : undefined}
          >
            {discovery()}
          </StyledMobilePanel>
        )}
      </StyledContainer>
    )
  }

  // desktop a tablet: pole ostáva v hlavičke, rozbalené sa rozšíri a otvorí sa pod ním panel
  return (
    <StyledContainer ref={containerRef}>
      <StyledDesktopAnchor data-compact={isCompact}>
        <StyledDesktopFloating
          data-open={isOpen}
          data-expand-left={expandToLeft}
        >
          {searchBox}
          {isOpen && <StyledDesktopPanel>{discovery(true)}</StyledDesktopPanel>}
        </StyledDesktopFloating>
      </StyledDesktopAnchor>
      {isOpen && <StyledOverlay onMouseDown={collapse} />}
    </StyledContainer>
  )
}

export default ProductSearchAi

const StyledContainer = styled.div<{ isMobile?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  ${({ isMobile }) => !isMobile && 'z-index: 1001;'}

  // iOS s klávesnicou skráti panel na viditeľnú výšku, stránka pod ním by presvitala
  // cez priesvitné lišty nad klávesnicou; biele pozadie ju zakryje až po spodok obrazovky
  &[data-open='true']::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 100vh;
    height: 100lvh;
    background: #fff;
  }
`

const StyledBarWrapper = styled.div<{ isMobile?: boolean; isOpen?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  ${({ isMobile }) =>
    isMobile ? 'padding: 0 18px; z-index: 2;' : 'z-index: 1001;'}
  ${({ isOpen }) => isOpen && 'background: #fff;'}
`

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background-color: #000;
  opacity: 0.4;
`

const panelIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
`

const StyledMobilePanel = styled.div`
  position: relative;
  animation: ${panelIn} 0.25s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  width: 100%;
  height: calc(100vh - 98px);
  height: calc(100dvh - 98px);
  box-sizing: border-box;
  padding: 0 18px;
  padding-bottom: calc(32px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: #fff;
`

const AI_SEARCH_MAX_WIDTH = 740

const StyledDesktopAnchor = styled.div`
  position: relative;
  width: 100%;
  height: 55px;

  &[data-compact='true'] {
    height: 40px;
  }
`

const StyledDesktopFloating = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 1001;
  width: 100%;
  max-width: ${AI_SEARCH_MAX_WIDTH}px;
  transform: translateX(-50%);
  transition: width 0.3s ease;

  &[data-expand-left='true'] {
    left: auto;
    right: 0;
    transform: none;
  }

  &[data-open='true'] {
    width: min(${AI_SEARCH_MAX_WIDTH}px, calc(100vw - 32px));
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const StyledDesktopPanel = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${AI_SEARCH_MAX_WIDTH}px;
  max-height: calc(100vh - 220px);
  margin-top: 10px;
  padding: 4px 24px 24px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #eaeaec;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  animation: ${panelIn} 0.25s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const StyledError = styled.p`
  margin: 16px 0 0;
  color: #a32d4a;
  font-size: 13px;
  line-height: 1.5;
`
